import { useState } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import { getRecipeForCooking, scaleRecipe } from 'wasp/client/operations';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { RecipeScaler } from '../../../components/recipe/RecipeScaler';
import { CustomServingInput } from './CustomServingInput';
import { Calculator, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Minimal summary type for initial prop
interface RecipeSummary {
  id: string;
  title: string;
  servings: number | null;
  heroImageUrl: string | null;
}

interface RecipeScalerDialogProps {
  recipe: RecipeSummary | null;
  open: boolean;
  onClose: () => void;
  onRecipeSaved?: () => void;
}

/**
 * Recipe Scaler Dialog Component
 * 
 * Enhanced modal for scaling recipes with three modes:
 * 1. Quick Presets - Common serving sizes
 * 2. Incremental - +/- buttons
 * 3. Custom - Text input for any number (events, catering)
 * 
 * Fetches full recipe details on-demand for accurate scaling.
 */
export function RecipeScalerDialog({ recipe, open, onClose, onRecipeSaved }: RecipeScalerDialogProps) {
  const [scaleMode, setScaleMode] = useState<'presets' | 'incremental' | 'custom'>('presets');
  const [customServings, setCustomServings] = useState<number | null>(null);
  const [customTitle, setCustomTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Action for saving scaled recipe
  const scaleRecipeFn = useAction(scaleRecipe);

  // Fetch full recipe data when dialog opens
  const { data, isLoading, error } = useQuery(
    getRecipeForCooking,
    { recipeId: recipe?.id || '' },
    { enabled: open && !!recipe?.id }
  );

  if (!recipe) return null;

  const presetServings = [2, 4, 6, 8, 12, 24, 50, 100];

  // Loading state
  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-coral" />
            <span className="ml-3 text-muted-foreground">Loading recipe...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Error state
  if (error || !data?.recipe) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl">
          <div className="text-center py-12">
            <p className="text-red-600">Failed to load recipe details</p>
            <Button onClick={onClose} className="mt-4">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Transform to RecipeScaler-compatible format
  const detailedRecipe = data.recipe;
  const baseServings = detailedRecipe.servings ?? 2;
  
  const scalerRecipe = {
    id: detailedRecipe.id,
    title: detailedRecipe.title,
    servings: baseServings,
    totalMinutes: detailedRecipe.totalMinutes ?? undefined,
    difficulty: detailedRecipe.difficulty ?? undefined,
    ingredients: detailedRecipe.ingredients.map((ri: any) => ({
      id: ri.id,
      name: ri.ingredient.name,
      quantity: ri.displayQty ?? ri.quantity,
      unit: ri.displayUnit ?? ri.ingredient.baseUnit ?? '',
      notes: ri.notes ?? undefined,
    })),
    steps: detailedRecipe.steps.map((s: any) => ({
      id: s.id,
      index: s.index,
      text: s.text,
      durationSeconds: s.durationSeconds ?? undefined,
    })),
  };

  const handlePresetClick = (servings: number) => {
    setCustomServings(servings);
  };

  const handleSaveScaledRecipe = async () => {
    if (!customServings || customServings === baseServings) {
      toast.error('Please scale the recipe before saving');
      return;
    }

    setIsSaving(true);
    try {
      const result: any = await scaleRecipeFn({
        recipeId: recipe.id,
        targetServings: customServings,
        saveAsUserRecipe: true,
        customTitle: customTitle || `${detailedRecipe.title} (${customServings} servings)`,
      });

      toast.success(`Saved "${customTitle || detailedRecipe.title}" to your recipes!`);
      onRecipeSaved?.();
      onClose();
    } catch (error) {
      console.error('Failed to save scaled recipe:', error);
      toast.error('Failed to save scaled recipe');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calculator className="h-5 w-5 text-coral" />
            Scale Recipe: {detailedRecipe.title}
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-4">
          {/* Left Panel: Recipe Info */}
          <div className="space-y-4">
            <div>
              {detailedRecipe.heroImageUrl && (
                <img 
                  src={detailedRecipe.heroImageUrl} 
                  alt={detailedRecipe.title}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
              )}
              <h3 className="font-semibold text-lg text-martinique mb-1">
                {detailedRecipe.title}
              </h3>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <span className="font-medium">Original:</span> 
                {baseServings} {baseServings === 1 ? 'serving' : 'servings'}
              </p>
            </div>
            
            {/* Original ingredients list */}
            <div className="space-y-2 border-t pt-4">
              <h4 className="font-medium text-martinique">Original Ingredients:</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {scalerRecipe.ingredients.map((ing: any, idx: number) => (
                  <div 
                    key={idx} 
                    className="flex justify-between items-start text-sm p-2 rounded bg-cream"
                  >
                    <span className="flex-1">{ing.name}</span>
                    <span className="text-muted-foreground font-medium ml-2 shrink-0">
                      {ing.quantity} {ing.unit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Panel: Scaler Interface */}
          <div className="space-y-4">
            <Tabs value={scaleMode} onValueChange={(v) => setScaleMode(v as any)}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="presets">Quick</TabsTrigger>
                <TabsTrigger value="incremental">+/-</TabsTrigger>
                <TabsTrigger value="custom">
                  Custom <span className="ml-1">⭐</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="presets" className="space-y-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  Select a common serving size:
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {presetServings.map((servings) => (
                    <Button
                      key={servings}
                      variant="outline"
                      onClick={() => handlePresetClick(servings)}
                      className={
                        customServings === servings 
                          ? 'bg-coral text-white hover:bg-coral/90 border-coral' 
                          : 'hover:border-coral'
                      }
                    >
                      {servings}
                    </Button>
                  ))}
                </div>
                {customServings && customServings !== baseServings && (
                  <div className="mt-4 p-4 bg-sage/10 border border-sage/20 rounded-lg">
                    <p className="text-sm font-medium text-sage-800 mb-2">
                      Scaling from {baseServings} to {customServings} servings
                    </p>
                    <p className="text-xs text-sage-600">
                      Factor: {(customServings / baseServings).toFixed(2)}x
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="incremental" className="mt-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Use +/- buttons to adjust servings:
                </p>
                {/* Use existing RecipeScaler component */}
                <RecipeScaler 
                  recipe={scalerRecipe}
                  onScaledRecipe={(scaled) => {
                    console.log('Scaled recipe:', scaled);
                    setCustomServings(scaled.servings);
                  }}
                />
              </TabsContent>

              <TabsContent value="custom" className="space-y-4 mt-4">
                <CustomServingInput 
                  onServingsChange={(servings) => {
                    setCustomServings(servings);
                  }}
                  minServings={1}
                  maxServings={10000}
                />
                
                {customServings && customServings !== baseServings && (
                  <div className="mt-4">
                    <RecipeScaler 
                      recipe={{ ...scalerRecipe, servings: customServings }}
                      onScaledRecipe={(scaled) => console.log('Custom scaled:', scaled)}
                    />
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Custom Title Input */}
            {customServings && customServings !== baseServings && (
              <div className="space-y-2 pt-4 border-t">
                <label htmlFor="custom-title" className="text-sm font-medium">
                  Custom Name (optional)
                </label>
                <Input
                  id="custom-title"
                  placeholder={`${detailedRecipe.title} (${customServings} servings)`}
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to use default name
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 pt-4 border-t">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1"
                disabled={isSaving}
              >
                Close
              </Button>
              <Button
                onClick={handleSaveScaledRecipe}
                disabled={!customServings || customServings === baseServings || isSaving}
                className="flex-1 bg-coral hover:bg-coral/90"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Scaled Recipe'
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
